/**
 * AgentRail — 64px 아이콘 Rail (좌측 고정)
 *
 * 중앙 레지스트리(rail-items.tsx)의 RailEntry[] 배열을 받아 렌더링.
 * separator, Tooltip, 하단 아이템을 선언적으로 처리.
 * sm(<640px) 에서는 숨김.
 */

import { forwardRef } from "react";
import { ChevronDown } from "lucide-react";
import { Link, useLocation } from "@tanstack/react-router";
import {
  BrandLogo,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/shared/components";
import { cn } from "@/shared/lib/utils";
import type { RailEntry } from "./rail-items";

export interface RailItem {
  id: string;
  icon: React.ReactNode;
  label: string;
  to?: string;
  onClick?: () => void;
  badge?: number;
}

export interface AgentRailProps {
  /** 메인 Rail 영역 (상단 nav). separator 포함 가능. */
  main: RailEntry[];
  /** 하단 고정 영역 (도움말, 마이페이지 등). 생략 시 표시 안 함. */
  bottom?: RailItem[];
  activeItem?: string;
  onItemClick?: (id: string) => void;
  className?: string;
}

export interface RailButtonProps {
  item: RailItem;
  isActive: boolean;
  onClick: () => void;
}

export const RailButton = forwardRef<HTMLElement, RailButtonProps>(
  function RailButton({ item, isActive, onClick, ...rest }, ref) {
    const buttonClass = cn(
      "relative flex size-12 flex-col items-center justify-center rounded-xl transition-colors",
      isActive
        ? "bg-accent/10 text-text-brand"
        : "text-text-tertiary hover:bg-surface-secondary hover:text-text-primary",
    );

    const badge =
      item.badge !== undefined && item.badge > 0 ? (
        <span className="absolute right-1.5 top-1.5 flex size-4 items-center justify-center rounded-full bg-signal-danger text-[9px] font-bold text-text-inverse">
          {item.badge > 9 ? "9+" : item.badge}
        </span>
      ) : null;

    if (item.to) {
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          to={item.to}
          title={item.label}
          onClick={onClick}
          className={buttonClass}
          {...rest}
        >
          {item.icon}
          {badge}
        </Link>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        onClick={onClick}
        title={item.label}
        className={buttonClass}
        {...rest}
      >
        {item.icon}
        {badge}
      </button>
    );
  },
);

function RailItemWithTooltip({
  item,
  isActive,
  onClick,
}: {
  item: RailItem;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <RailButton item={item} isActive={isActive} onClick={onClick} />
      </TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}

export function AgentRail({
  main,
  bottom,
  activeItem,
  onItemClick,
  className,
}: AgentRailProps) {
  const location = useLocation();
  const currentPath = location.pathname;

  function isItemActive(item: RailItem): boolean {
    if (activeItem) return activeItem === item.id;
    if (item.to) {
      return currentPath === item.to || currentPath.startsWith(item.to + "/");
    }
    return false;
  }

  function handleClick(item: RailItem) {
    onItemClick?.(item.id);
    item.onClick?.();
  }

  return (
    <TooltipProvider delayDuration={300}>
      <aside
        className={cn(
          "hidden sm:flex flex-col items-center",
          "w-16 shrink-0 border-r border-stroke-subtle bg-surface-raised",
          "py-3",
          className,
        )}
      >
        {/* Logo */}
        <div className="mb-4 flex size-12 items-center justify-center">
          <Link to="/" title="홈">
            <BrandLogo className="size-6 text-text-brand" />
          </Link>
        </div>

        {/* Main items */}
        <nav className="flex flex-1 flex-col items-center gap-1">
          {main.map((entry, i) => {
            if (entry.type === "separator") {
              return (
                <div key={`sep-${i}`} className="my-1 h-px w-8 bg-border" />
              );
            }
            return (
              <RailItemWithTooltip
                key={entry.id}
                item={entry}
                isActive={isItemActive(entry)}
                onClick={() => handleClick(entry)}
              />
            );
          })}
        </nav>

        {/* Bottom items */}
        {bottom && bottom.length > 0 && (
          <div className="flex flex-col items-center gap-1">
            {bottom.map((item) => (
              <RailItemWithTooltip
                key={item.id}
                item={item}
                isActive={isItemActive(item)}
                onClick={() => handleClick(item)}
              />
            ))}
          </div>
        )}
      </aside>
    </TooltipProvider>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   SlideOverPanel — Rail 아이콘 클릭 시 열리는 280px 오버레이 패널
   ══════════════════════════════════════════════════════════════════════════ */

export interface SlideOverPanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  className?: string;
}

export function SlideOverPanel({
  open,
  onClose,
  title,
  children,
  className,
}: SlideOverPanelProps) {
  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/20 sm:hidden"
          onClick={onClose}
        />
      )}
      <div
        className={cn(
          "fixed left-16 top-0 z-40 flex h-full flex-col border-r border-stroke-subtle bg-surface-raised shadow-lg transition-all duration-200",
          open
            ? "w-[280px] translate-x-0 pointer-events-auto"
            : "w-[280px] -translate-x-full opacity-0 pointer-events-none",
          className,
        )}
        style={{ maxWidth: "360px" }}
      >
        <div className="flex h-14 items-center justify-between border-b border-stroke-subtle px-4">
          <span className="text-[13px] font-semibold text-text-primary">
            {title}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="flex size-7 items-center justify-center rounded-md text-text-tertiary hover:bg-surface-secondary hover:text-text-primary"
          >
            <ChevronDown className="size-4 -rotate-90" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </>
  );
}
